"use strict";

import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useProjectMediaPlayback } from "../../hooks/useProjectMediaPlayback";
import { getLanguageName } from "../../utils/generalFunctions.js";

const fileNameFromObjectName = (objectName) =>
  (objectName || "").split("/").pop();

const SubtitleFileRow = ({ role, languageCode, objectName, projectId }) => {
  const { playbackUrl, playbackUnavailable } = useProjectMediaPlayback({
    reference: objectName,
    projectId,
  });

  return (
    <TableRow>
      <TableCell>{role}</TableCell>
      <TableCell>{languageCode ? getLanguageName(languageCode) : "—"}</TableCell>
      <TableCell sx={{ wordBreak: "break-all" }}>
        {fileNameFromObjectName(objectName)}
      </TableCell>
      <TableCell>
        {playbackUrl ? (
          <a
            href={playbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1976d2" }}
          >
            Open
          </a>
        ) : playbackUnavailable ? (
          "Unavailable"
        ) : (
          "Loading..."
        )}
      </TableCell>
    </TableRow>
  );
};

/**
 * Lists the subtitle files currently attached to a project.
 */
const ProjectSubtitleFiles = ({
  projectId,
  originalSubtitleObjectName,
  translatedSubtitles,
}) => {
  const subtitleFiles = [
    ...(originalSubtitleObjectName
      ? [{ key: "original", role: "Original", objectName: originalSubtitleObjectName }]
      : []),
    ...Object.entries(translatedSubtitles || {})
      .sort(([a], [b]) => getLanguageName(a).localeCompare(getLanguageName(b)))
      .map(([languageCode, objectName]) => ({
        key: languageCode,
        role: "Translated",
        languageCode,
        objectName,
      })),
  ];

  return (
    <Box>
      <Typography variant="h6" fontWeight={600}>
        Available subtitle files
      </Typography>
      <Typography variant="body2" color="text.secondary" mt={0.5} mb={1.5}>
        SRT files currently attached to this project.
      </Typography>

      {subtitleFiles.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No subtitle files uploaded yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table
            size="small"
            sx={{
              minWidth: 650,
              bgcolor: "white",
              "& .MuiTableCell-root": { py: 0.5, px: 1 },
            }}
          >
            <TableHead bgcolor="#5570dc">
              <TableRow>
                <TableCell>ROLE</TableCell>
                <TableCell>LANGUAGE</TableCell>
                <TableCell>FILE</TableCell>
                <TableCell>LINK</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subtitleFiles.map(({ key, ...subtitle }) => (
                <SubtitleFileRow key={key} projectId={projectId} {...subtitle} />
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default ProjectSubtitleFiles;
